import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { formatCategory } from '../lib/utils';
import { 
  Package, 
  IndianRupee, 
  TrendingUp, 
  TrendingDown, 
  Warehouse, 
  RefreshCw, 
  Download, 
  Search,
  Filter,
  Layers,
  Eye,
  ArrowUpDown,
  Clock,
  Tags,
  Factory
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Sortable header component
const SortableHeader = ({ label, sortKey, onSort }) => (
  <TableHead 
    className="cursor-pointer hover:bg-muted/50"
    onClick={() => onSort(sortKey)}
  >
    <div className="flex items-center gap-1">
      {label}
      <ArrowUpDown className="w-3 h-3 opacity-50" />
    </div>
  </TableHead>
);

export default function InventoryReportPage() {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [excludeCTM, setExcludeCTM] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'item_name', direction: 'asc' });
  const [selectedItem, setSelectedItem] = useState(null);
  const [batchDetails, setBatchDetails] = useState(null);
  const [batchLoading, setBatchLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchReport = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/inventory-report`, {
        headers: getAuthHeader()
      });
      setReportData(response.data);
      setLastUpdated(new Date());
    } catch (error) {
      toast.error('Failed to load inventory report');
      console.error('Report fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
    // Auto-refresh removed - was causing unnecessary load every 30 seconds
    // Users can manually refresh using the Refresh button
  }, []);

  const fetchBatchDetails = async (itemId) => {
    setBatchLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/inventory-batches/${itemId}`, {
        headers: getAuthHeader()
      });
      setBatchDetails(response.data);
    } catch (error) {
      toast.error('Failed to load batch details');
    } finally {
      setBatchLoading(false);
    }
  };

  const handleViewBatches = (item) => {
    setSelectedItem(item);
    fetchBatchDetails(item.item_id);
  };

  // Sorting function
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Get unique categories
  const categories = useMemo(() => {
    if (!reportData?.items) return [];
    const cats = [...new Set(reportData.items.map(i => i.category).filter(Boolean))];
    return cats.sort();
  }, [reportData]);

  // Filter and sort items
  const filteredItems = useMemo(() => {
    if (!reportData?.items) return [];
    
    let items = reportData.items.filter(item => {
      const matchesSearch = 
        (item.item_code || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item.item_name || '').toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = categoryFilter === 'all' || item.category === categoryFilter;
      const passesCTMFilter = !excludeCTM || (item.category || '').toUpperCase() !== 'CTM';
      return matchesSearch && matchesCategory && passesCTMFilter;
    });

    // Sort
    items.sort((a, b) => {
      let aVal = a[sortConfig.key] || '';
      let bVal = b[sortConfig.key] || '';
      
      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = (bVal || '').toLowerCase();
      }
      
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return items;
  }, [reportData, searchTerm, categoryFilter, excludeCTM, sortConfig]);

  // Calculate filtered totals
  const filteredTotals = useMemo(() => {
    return {
      total_opening: filteredItems.reduce((sum, i) => sum + i.opening_stock_qty, 0),
      total_opening_amount: filteredItems.reduce((sum, i) => sum + (i.opening_stock_amount || 0), 0),
      total_incoming: filteredItems.reduce((sum, i) => sum + i.total_incoming_qty, 0),
      total_outgoing: filteredItems.reduce((sum, i) => sum + i.total_outgoing_qty, 0),
      total_wip_rmt_amount: filteredItems.reduce((sum, i) => sum + (i.wip_rmt_amount || 0), 0),
      total_stock_in_hand: filteredItems.reduce((sum, i) => sum + i.ready_stock, 0),
      total_amount: filteredItems.reduce((sum, i) => sum + i.stock_in_hand_amount, 0)
    };
  }, [filteredItems]);

  const exportToCSV = () => {
    if (!filteredItems.length) return;

    const headers = [
      'Item Code', 'Item Name', 'Category', 'Unit',
      'Opening Stock', 'Opening Stock Value', 'Total Incoming', 'Total Outgoing',
      'Stock in Hand', 'Bin Card Stock', 'Avg Price', 'Stock-in-Hand Value'
    ];

    const rows = filteredItems.map(item => [
      item.item_code, 
      item.item_name, 
      item.category, 
      item.unit,
      item.opening_stock_qty,
      item.opening_stock_amount,
      item.total_incoming_qty, 
      item.total_outgoing_qty,
      item.ready_stock,
      ['RMT', 'CTM'].includes(item.category) ? (item.bin_card_stock || 0) : '',
      item.avg_price_per_unit, 
      item.stock_in_hand_amount
    ]);

    const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `inventory_report_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Report exported successfully');
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value || 0);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-IN').format(value || 0);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Warehouse className="w-6 h-6 text-primary" />
              Inventory Report
            </h1>
            <p className="text-muted-foreground mt-1">
              Stock position and valuation summary
            </p>
          </div>
          <div className="flex items-center gap-2">
            {lastUpdated && (
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <Clock className="w-3 h-3" />
                Last updated: {lastUpdated.toLocaleTimeString()}
              </span>
            )}
            <Button variant="outline" size="sm" onClick={fetchReport} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button variant="outline" size="sm" onClick={exportToCSV} disabled={!filteredItems.length}>
              <Download className="w-4 h-4 mr-1" />
              Export CSV
            </Button>
          </div>
        </div>

        {/* Summary Cards - Simplified */}
        {reportData && (
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-3">
            <Card data-testid="summary-card-opening-stock">
              <CardContent className="pt-4 pb-4 px-4">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-muted-foreground">Opening Stock</p>
                    <p className="text-lg font-bold">{formatNumber(filteredTotals.total_opening)}</p>
                  </div>
                  <Package className="w-6 h-6 text-gray-300 shrink-0" />
                </div>
              </CardContent>
            </Card>

            <Card data-testid="summary-card-opening-stock-value">
              <CardContent className="pt-4 pb-4 px-4">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-muted-foreground">Opening Stock Value</p>
                    <p className="text-lg font-bold">{formatCurrency(filteredTotals.total_opening_amount)}</p>
                  </div>
                  <IndianRupee className="w-6 h-6 text-gray-300 shrink-0" />
                </div>
              </CardContent>
            </Card>

            <Card data-testid="summary-card-total-incoming">
              <CardContent className="pt-4 pb-4 px-4">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-muted-foreground">Total Incoming</p>
                    <p className="text-lg font-bold text-green-600">+{formatNumber(filteredTotals.total_incoming)}</p>
                  </div>
                  <TrendingUp className="w-6 h-6 text-green-300 shrink-0" />
                </div>
              </CardContent>
            </Card>
            
            <Card data-testid="summary-card-total-outgoing">
              <CardContent className="pt-4 pb-4 px-4">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-muted-foreground">Total Outgoing</p>
                    <p className="text-lg font-bold text-red-600">-{formatNumber(filteredTotals.total_outgoing)}</p>
                  </div>
                  <TrendingDown className="w-6 h-6 text-red-300 shrink-0" />
                </div>
              </CardContent>
            </Card>

            <Card data-testid="summary-card-wip-rmt-value">
              <CardContent className="pt-4 pb-4 px-4">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-muted-foreground">WIP (RMT)</p>
                    <p className="text-lg font-bold text-amber-600">{formatCurrency(filteredTotals.total_wip_rmt_amount)}</p>
                  </div>
                  <Factory className="w-6 h-6 text-amber-300 shrink-0" />
                </div>
              </CardContent>
            </Card>
            
            <Card data-testid="summary-card-stock-in-hand">
              <CardContent className="pt-4 pb-4 px-4">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-muted-foreground">Stock in Hand</p>
                    <p className="text-lg font-bold text-blue-600">{formatNumber(filteredTotals.total_stock_in_hand)}</p>
                  </div>
                  <Warehouse className="w-6 h-6 text-blue-300 shrink-0" />
                </div>
              </CardContent>
            </Card>
            
            <Card data-testid="summary-card-stock-in-hand-value">
              <CardContent className="pt-4 pb-4 px-4">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-muted-foreground">Stock-in-Hand Value</p>
                    <p className="text-lg font-bold text-blue-700">{formatCurrency(filteredTotals.total_amount)}</p>
                  </div>
                  <IndianRupee className="w-6 h-6 text-blue-300 shrink-0" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20" data-testid="summary-card-inventory-value">
              <CardContent className="pt-4 pb-4 px-4">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-muted-foreground font-medium">Inventory Value</p>
                    <p className="text-lg font-bold text-primary">{formatCurrency(filteredTotals.total_amount + filteredTotals.total_wip_rmt_amount)}</p>
                  </div>
                  <IndianRupee className="w-6 h-6 text-primary/40 shrink-0" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
            <Input
              placeholder="Search by item code or name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="inventory-search"
            />
          </div>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-full sm:w-48" data-testid="category-filter">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map(cat => (
                <SelectItem key={cat} value={cat}>{formatCategory(cat)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <label
            className="inline-flex items-center gap-2 px-3 h-10 rounded-md border border-input bg-background text-sm cursor-pointer hover:bg-muted/50 transition-colors select-none"
            data-testid="exclude-ctm-toggle-label"
            title="Hide CTM (Consumable Tools & Materials) items from the report. Affects summary cards, table, and CSV export."
          >
            <input
              type="checkbox"
              checked={excludeCTM}
              onChange={(e) => setExcludeCTM(e.target.checked)}
              className="h-4 w-4 accent-primary cursor-pointer"
              data-testid="exclude-ctm-toggle"
            />
            <span>Exclude CTM</span>
          </label>
        </div>

        {/* Simplified Main Table */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center justify-between">
              <span>Inventory Details ({filteredItems.length} items)</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead>Name</TableHead>
                    <SortableHeader label="Category" sortKey="category" onSort={handleSort} />
                    <TableHead>Unit</TableHead>
                    <TableHead className="text-right">Opening Stock</TableHead>
                    <TableHead className="text-right">Total Incoming</TableHead>
                    <TableHead className="text-right">Total Outgoing</TableHead>
                    <SortableHeader label="Stock in Hand" sortKey="ready_stock" onSort={handleSort} />
                    <TableHead className="text-right">Bin Card Stock</TableHead>
                    <SortableHeader label="Amount" sortKey="stock_in_hand_amount" onSort={handleSort} />
                    <TableHead className="text-center">Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={10} className="text-center py-8">
                        <div className="flex items-center justify-center gap-2">
                          <RefreshCw className="w-5 h-5 animate-spin" />
                          Loading inventory data...
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : filteredItems.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} className="text-center py-8 text-muted-foreground">
                        No items found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredItems.map((item) => (
                      <TableRow key={item.item_id} className="hover:bg-muted/30">
                        <TableCell className="max-w-[150px] truncate font-medium" title={item.item_name}>
                          {item.item_name}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">
                            {formatCategory(item.category)}
                          </Badge>
                        </TableCell>
                        <TableCell>{item.unit || '-'}</TableCell>
                        <TableCell className="text-right">{formatNumber(item.opening_stock_qty)}</TableCell>
                        <TableCell className="text-right text-green-600">
                          +{formatNumber(item.total_incoming_qty)}
                        </TableCell>
                        <TableCell className="text-right text-red-600">
                          -{formatNumber(item.total_outgoing_qty)}
                        </TableCell>
                        <TableCell className="text-right font-bold">{formatNumber(item.ready_stock)}</TableCell>
                        <TableCell className={`text-right ${item.category === 'RMT' && Math.abs((item.bin_card_stock || 0) - item.ready_stock) > 0.01 ? 'text-red-600 font-bold' : ''}`}>
                          {['RMT', 'CTM'].includes(item.category) ? formatNumber(item.bin_card_stock || 0) : <span className="text-muted-foreground">-</span>}
                        </TableCell>
                        <TableCell className="text-right font-bold text-primary">
                          {formatCurrency(item.stock_in_hand_amount)}
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => handleViewBatches(item)}
                              data-testid={`view-batches-${item.item_code}`}
                              title="View batch details"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            {item.has_variants && (
                              <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200 text-xs">
                                <Tags className="w-3 h-3 mr-1" />
                                Variants
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                  {/* Totals Row */}
                  {filteredItems.length > 0 && (
                    <TableRow className="bg-muted/70 font-bold border-t-2">
                      <TableCell colSpan={4} className="text-right">TOTAL</TableCell>
                      <TableCell className="text-right">{formatNumber(filteredTotals.total_opening)}</TableCell>
                      <TableCell className="text-right text-green-600">+{formatNumber(filteredTotals.total_incoming)}</TableCell>
                      <TableCell className="text-right text-red-600">-{formatNumber(filteredTotals.total_outgoing)}</TableCell>
                      <TableCell className="text-right">{formatNumber(filteredTotals.total_stock_in_hand)}</TableCell>
                      <TableCell></TableCell>
                      <TableCell className="text-right text-primary">{formatCurrency(filteredTotals.total_amount)}</TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Batch Details Dialog */}
        <Dialog open={!!selectedItem} onOpenChange={() => { setSelectedItem(null); setBatchDetails(null); }}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Layers className="w-5 h-5 text-primary" />
                Batch Details - {selectedItem?.item_name}
              </DialogTitle>
            </DialogHeader>

            {batchLoading ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="w-6 h-6 animate-spin" />
              </div>
            ) : batchDetails ? (
              <div className="space-y-4">
                {/* Summary */}
                <Card className="bg-muted/30">
                  <CardContent className="pt-4">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-muted-foreground">Total Qty</p>
                        <p className="font-bold">{batchDetails.summary?.total_qty}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Total Value</p>
                        <p className="font-bold">{formatCurrency(batchDetails.summary?.total_value)}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Weighted Avg</p>
                        <p className="font-bold">{formatCurrency(batchDetails.summary?.weighted_avg_price)}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Last Price</p>
                        <p className="font-bold">{formatCurrency(batchDetails.summary?.last_batch_price)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Batches Table */}
                <div>
                  <h3 className="text-sm font-semibold mb-2">Active Batches ({batchDetails.batches?.length || 0})</h3>
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead>Date</TableHead>
                        <TableHead>Source</TableHead>
                        <TableHead>Reference No.</TableHead>
                        <TableHead className="text-right">Received</TableHead>
                        <TableHead className="text-right">Remaining</TableHead>
                        <TableHead className="text-right">Unit Price</TableHead>
                        <TableHead className="text-right">Batch Value</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {batchDetails.batches?.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center py-4 text-muted-foreground">
                            No batches found
                          </TableCell>
                        </TableRow>
                      ) : (
                        batchDetails.batches?.map((batch, idx) => (
                          <TableRow key={batch.id || idx}>
                            <TableCell className="text-xs text-muted-foreground">
                              {batch.batch_date ? new Date(batch.batch_date).toLocaleDateString('en-GB') : '-'}
                            </TableCell>
                            <TableCell>
                              <Badge variant={
                                batch.source_type === 'opening_stock' ? 'default' :
                                batch.source_type === 'purchase' ? 'secondary' :
                                batch.source_type === 'production' ? 'outline' :
                                'destructive'
                              }>
                                {batch.source_type === 'opening_stock' ? 'Opening' :
                                 batch.source_type === 'purchase' ? 'Purchase' :
                                 batch.source_type === 'production' ? 'Production' :
                                 batch.source_type}
                              </Badge>
                            </TableCell>
                            <TableCell className="font-mono text-sm">{batch.source_ref || '-'}</TableCell>
                            <TableCell className="text-right">{batch.qty_received}</TableCell>
                            <TableCell className="text-right font-medium">{batch.qty_remaining}</TableCell>
                            <TableCell className="text-right">{formatCurrency(batch.unit_price)}</TableCell>
                            <TableCell className="text-right font-medium">
                              {formatCurrency(batch.qty_remaining * batch.unit_price)}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>

                {/* Variant Breakdown */}
                {(batchDetails.variants?.length > 0 || (selectedItem?.has_variants && Object.keys(selectedItem.stock_by_variant || {}).length > 0)) && (
                  <div>
                    <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
                      <Tags className="w-4 h-4 text-purple-600" />
                      Variant Stock Breakdown
                    </h3>
                    <Card className="bg-purple-50/50 border-purple-200">
                      <CardContent className="pt-4">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-purple-100/50">
                              <TableHead>Variant Code</TableHead>
                              <TableHead className="text-right">Stock Qty</TableHead>
                              <TableHead className="text-right">Unit Price</TableHead>
                              <TableHead className="text-right">Total Value</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {(batchDetails.variants?.length > 0 ? batchDetails.variants : 
                              Object.entries(selectedItem?.stock_by_variant || {}).map(([code, qty]) => ({
                                variant_code: code,
                                stock_qty: qty,
                                unit_price: selectedItem?.avg_price_per_unit || 0
                              }))
                            ).map((variant, idx) => (
                              <TableRow key={variant.variant_code || idx}>
                                <TableCell className="font-mono text-sm text-purple-700 font-medium">
                                  {variant.variant_code}
                                </TableCell>
                                <TableCell className="text-right font-medium">{variant.stock_qty}</TableCell>
                                <TableCell className="text-right">{formatCurrency(variant.unit_price)}</TableCell>
                                <TableCell className="text-right font-medium text-purple-700">
                                  {formatCurrency(variant.stock_qty * variant.unit_price)}
                                </TableCell>
                              </TableRow>
                            ))}
                            {/* Total Row */}
                            <TableRow className="bg-purple-100/70 font-bold border-t">
                              <TableCell>TOTAL</TableCell>
                              <TableCell className="text-right">
                                {(batchDetails.variants?.length > 0 ? batchDetails.variants : 
                                  Object.entries(selectedItem?.stock_by_variant || {}).map(([code, qty]) => ({
                                    stock_qty: qty
                                  }))
                                ).reduce((sum, v) => sum + (v.stock_qty || 0), 0)}
                              </TableCell>
                              <TableCell></TableCell>
                              <TableCell className="text-right text-purple-700">
                                {formatCurrency(
                                  (batchDetails.variants?.length > 0 ? batchDetails.variants : 
                                    Object.entries(selectedItem?.stock_by_variant || {}).map(([code, qty]) => ({
                                      stock_qty: qty,
                                      unit_price: selectedItem?.avg_price_per_unit || 0
                                    }))
                                  ).reduce((sum, v) => sum + (v.stock_qty * (v.unit_price || 0)), 0)
                                )}
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* Recent Consumptions */}
                {batchDetails.consumptions?.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold mb-2">Recent Consumptions</h3>
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted/50">
                          <TableHead>Type</TableHead>
                          <TableHead className="text-right">Qty</TableHead>
                          <TableHead className="text-right">Price</TableHead>
                          <TableHead className="text-right">Value</TableHead>
                          <TableHead>Date</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {batchDetails.consumptions.slice(0, 10).map((cons, idx) => (
                          <TableRow key={cons.id || idx}>
                            <TableCell>
                              <Badge variant="outline">{cons.consumed_for}</Badge>
                            </TableCell>
                            <TableCell className="text-right text-red-600">-{cons.qty_consumed}</TableCell>
                            <TableCell className="text-right">{formatCurrency(cons.unit_price)}</TableCell>
                            <TableCell className="text-right">{formatCurrency(cons.total_value)}</TableCell>
                            <TableCell className="text-xs text-muted-foreground">
                              {cons.created_at ? new Date(cons.created_at).toLocaleDateString('en-GB') : '-'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-center py-4 text-muted-foreground">Failed to load batch details</p>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => { setSelectedItem(null); setBatchDetails(null); }}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
